import React from "react";
import type { HistogramSeries } from "../utils/chartData";
import type { DrillClick } from "../utils/drilldown";
import { EmptyState } from "../../../ui/EmptyState";
import { ChartSkeleton } from "../../../ui/ChartSkeleton";
import { useChartTheme } from "../../theme";

interface RidgelineChartProps {
  series: HistogramSeries[];
  /** Number of uniform [0,1] score bins (server's histogram_bins). */
  binCount: number;
  /** criteria → display label (evaluator name) for row labels. */
  labels?: Record<string, string>;
  loading?: boolean;
  /** When provided, clicking a score band drills into the filtered Traces view. */
  onDrill?: (click: DrillClick) => void;
}

const TITLE = "Score distribution";
const FILL = "#107169"; // brand teal — matches the former histogram bars
const VBW = 1000; // viewBox width (arbitrary units; scales to the container)
const ROW_H = 48;
const PAD_T = 6; // headroom above the tallest peak
const BASE_Y = ROW_H - 2; // baseline
const GRID_COLS = "168px 1fr";

/** Catmull-Rom → cubic-bezier smoothing for a density outline. */
function smoothPath(pts: Array<[number, number]>): string {
  if (pts.length === 0) return "";
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(
      1,
    )}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

/**
 * Score distribution as a ridgeline: one compact density row per criterion on a
 * shared [0,1] score axis, so shape (skew / bimodality) reads at a glance down
 * the column — far more space-efficient than per-criterion histogram facets.
 * Each row's density is normalised to its own peak (shapes are comparable, not
 * absolute volume — hence the per-row n= and the exact count in each band's
 * tooltip). Consumes the same ungrouped `histogram=true` series
 * (bucketRowsToHistograms) as before, so it issues no request of its own. A
 * transparent per-bin overlay preserves the score-band drill into Traces.
 */
export function RidgelineChart({
  series,
  binCount,
  labels = {},
  loading = false,
  onDrill,
}: RidgelineChartProps) {
  const chart = useChartTheme();

  if (loading) {
    return <ChartSkeleton title={TITLE} variant="bar" />;
  }

  if (series.length === 0 || binCount <= 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h3 className="text-lg font-semibold mb-4">{TITLE}</h3>
        <EmptyState
          message="No evaluation data available for chart"
          variant="info"
        />
      </div>
    );
  }

  return (
    <div
      className="bg-card rounded-lg shadow-sm border border-border p-6"
      data-testid="ridgeline-chart"
    >
      <h3 className="text-lg font-semibold mb-4">{TITLE}</h3>

      <div className="flex flex-col gap-1">
        {series.map((s, rowIdx) => {
          const max = Math.max(1, ...s.counts);
          const y = (count: number) =>
            BASE_Y - (count / max) * (BASE_Y - PAD_T);
          // Plot each bin at its CENTRE so a peak lines up with the band the
          // click below drills into.
          const pts = s.counts.map(
            (c, i): [number, number] => [((i + 0.5) / binCount) * VBW, y(c)],
          );
          const line = smoothPath(pts);
          const area = `${line} L ${VBW} ${BASE_Y} L 0 ${BASE_Y} Z`;
          // Key the gradient by row index — criteria strings could collide once
          // stripped of non-word chars.
          const gradId = `ridge-grad-${rowIdx}`;
          return (
            <div
              key={s.criteria}
              data-testid="ridgeline-row"
              className="grid items-center gap-3"
              style={{ gridTemplateColumns: GRID_COLS }}
            >
              <div className="min-w-0">
                <div
                  className="text-sm font-medium truncate"
                  title={s.criteria}
                >
                  {labels[s.criteria] ?? s.criteria}
                </div>
                <div className="text-xs text-muted-foreground">
                  n={s.total.toLocaleString()}
                </div>
              </div>

              <svg
                width="100%"
                height={ROW_H}
                viewBox={`0 0 ${VBW} ${ROW_H}`}
                preserveAspectRatio="none"
                role="img"
                aria-label={`${labels[s.criteria] ?? s.criteria} score distribution`}
              >
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={FILL} stopOpacity="0.32" />
                    <stop offset="1" stopColor={FILL} stopOpacity="0.04" />
                  </linearGradient>
                </defs>
                <line
                  x1="0"
                  y1={BASE_Y}
                  x2={VBW}
                  y2={BASE_Y}
                  stroke={chart.grid}
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
                <path d={area} fill={`url(#${gradId})`} />
                <path
                  d={line}
                  fill="none"
                  stroke={FILL}
                  strokeWidth="1.6"
                  vectorEffect="non-scaling-stroke"
                />

                {/* Transparent per-bin hit areas keep the score-band drill and
                    expose the exact count via a tooltip. */}
                {s.counts.map((c, i) => {
                  const lo = i / binCount;
                  const hi = (i + 1) / binCount;
                  return (
                    <rect
                      key={i}
                      data-testid="ridge-band"
                      data-bin={i}
                      x={lo * VBW}
                      y={0}
                      width={(1 / binCount) * VBW}
                      height={ROW_H}
                      fill="transparent"
                      className={onDrill ? "cursor-pointer" : undefined}
                      onClick={
                        onDrill
                          ? () =>
                              onDrill({
                                type: "scoreBin",
                                criteria: s.criteria,
                                minScore: lo,
                                maxScore: hi,
                              })
                          : undefined
                      }
                    >
                      <title>{`${lo.toFixed(1)}–${hi.toFixed(
                        1,
                      )}: ${c.toLocaleString()}`}</title>
                    </rect>
                  );
                })}
              </svg>
            </div>
          );
        })}

        {/* Shared score axis under the density column. */}
        <div
          className="grid gap-3 mt-1"
          style={{ gridTemplateColumns: GRID_COLS }}
        >
          <div />
          <div className="relative h-4 text-[10px] text-muted-foreground">
            {[0, 0.25, 0.5, 0.75, 1].map((t) => (
              <span
                key={t}
                className="absolute font-mono"
                style={{
                  left: `${t * 100}%`,
                  transform:
                    t === 0
                      ? "none"
                      : t === 1
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                }}
              >
                {t.toFixed(2)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
